import { DateTimeConfigValue } from 'app/types';
import React, { useRef } from 'react';


export interface DateTimeConfigComponentProps {
  value: DateTimeConfigValue;
  onChange: (value: DateTimeConfigValue) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | string;
}

const DateTimeConfigComponent: React.FC<DateTimeConfigComponentProps> = ({
  value,
  onChange,
  className = "",
  size = "sm"
}) => {
  const handleModeChange = (mode: 'direct' | 'components') => {
    const newConfig: DateTimeConfigValue = {
      mode,
      offsetMinutes: value.offsetMinutes ?? -30,
      ...(mode === 'direct'
        ? { directPath: '' }
        : { componentPaths: { date: 'data.startDate', time: 'data.startTime', timezone: 'data.timezone' } }
      )
    };
    onChange(newConfig);
  };

  const handleDirectPathChange = (directPath: string) => {
    onChange({
      ...value,
      directPath
    });
  };

  const handleComponentPathChange = (pathType: 'date' | 'time' | 'timezone', pathValue: string) => {
    onChange({
      ...value,
      componentPaths: {
        ...(value.componentPaths || {}),
        [pathType]: pathValue
      }
    });
  };

  const handleOffsetChange = (offset: number) => {
    onChange({
      ...value,
      offsetMinutes: offset
    });
  };

  const inputClass = `form-control form-control-${size}`;
  const btnClass = `btn btn-outline-primary btn-${size}`;

  const radioGroupIdRef = useRef(`radio-group-${Math.random().toString(36).substr(2, 9)}`);
  const radioGroupId = radioGroupIdRef.current;

  return (
    <div className={`d-flex flex-column ${className}`}>
      {/* Mode Selection */}
      <div className="mb-2">
        <div className="btn-group" role="group">
          <input
            type="radio"
            className="btn-check"
            name={`dateTimeMode-${radioGroupId}`}
            id={`directMode-${radioGroupId}`}
            checked={value?.mode === 'direct'}
            onChange={() => handleModeChange('direct')}
          />
          <label
            className={`${btnClass} ${value?.mode === 'direct' ? 'active bg-primary text-white' : ''}`}
            htmlFor={`directMode-${radioGroupId}`}
            style={{ minWidth: 120 }}
          >
            Direct ISO
          </label>

          <input
            type="radio"
            className="btn-check"
            name={`dateTimeMode-${radioGroupId}`}
            id={`componentMode-${radioGroupId}`}
            checked={value?.mode === 'components'}
            onChange={() => handleModeChange('components')}
          />
          <label
            className={`${btnClass} ${value?.mode === 'components' ? 'active bg-primary text-white' : ''}`}
            htmlFor={`componentMode-${radioGroupId}`}
            style={{ minWidth: 160 }}
          >
            Build from Components
          </label>
        </div>
      </div>

      {/* Direct Mode Configuration */}
      {value?.mode === 'direct' && (
        <div className="mb-2 d-flex align-items-center gap-2">
          <div className="input-group" style={{ width: 260 }}>
            <span className="input-group-text fw-semibold" style={{ minWidth: 60 }}>Offset</span>
            <input
              type="number"
              className={inputClass}
              placeholder="Minutes"
              style={{ width: 70 }}
              min={-10080}
              max={10080}
              value={value?.offsetMinutes}
              onChange={e => handleOffsetChange(Number(e.target.value))}
            />
            <input
              type="text"
              className={inputClass}
              placeholder="data.fullDateTime"
              value={value?.directPath || ''}
              onChange={e => handleDirectPathChange(e.target.value)}
              style={{ width: 300 }}
            />
          </div>
        </div>
      )}
      {value?.mode === 'direct' && (
        <div className="form-text small ms-1">
          Path to ISO datetime string
        </div>
      )}

      {/* Component Mode Configuration */}
      {value?.mode === 'components' && (
        <div className="mb-2 d-flex align-items-center gap-2">
          <div className="input-group" style={{ width: 340 }}>
            <span className="input-group-text fw-semibold" style={{ minWidth: 60 }}>Offset</span>
            <input
              type="number"
              className={inputClass}
              placeholder="Minutes"
              style={{ width: 70 }}
              min={-10080}
              max={10080}
              value={value?.offsetMinutes}
              onChange={e => handleOffsetChange(Number(e.target.value))}
            />
            <input
              type="text"
              className={inputClass}
              placeholder="data.startDate"
              value={value?.componentPaths?.date || ''}
              onChange={e => handleComponentPathChange('date', e.target.value)}
              style={{ width: 300 }}
            />
            <input
              type="text"
              className={inputClass}
              placeholder="data.startTime"
              value={value?.componentPaths?.time || ''}
              onChange={e => handleComponentPathChange('time', e.target.value)}
              style={{ width: 300 }}
            />
            <input
              type="text"
              className={inputClass}
              placeholder="data.timezone"
              value={value?.componentPaths?.timezone || ''}
              onChange={e => handleComponentPathChange('timezone', e.target.value)}
              style={{ width: 300 }}
            />
          </div>
        </div>
      )}

      <div className="form-text small ms-1">
        Example: <b>-30</b> and <b>selected date path from payload</b> will send 30 minutes before the selected event.
      </div>
    </div>
  );
};

export default DateTimeConfigComponent;